import { differenceInDays, parseISO } from 'date-fns';
import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { useForm } from 'react-typed-form';

import { Api, useApiFormSubmit } from '../../api';
import { AccentTextLink, DateWithTooltip, Text } from '../../components';
import {
  FieldSelect,
  FieldText,
  FieldTextRequired,
  FormWrap,
  SubmitButton,
} from '../../form';
import { useMyUserClient } from '../../hooks';
import { Model } from '../../models';

type Props = Readonly<{
  buildEvent: {
    ['@id']: string;
    ['@type']: 'BuildEvent';
    id: string;
    type: string;
    percent: number | null;
    recordedAt: string;
  };
  isFirst: boolean;
  invalidate: () => unknown;
}>;

const AUTO_ROLLOUT_STAGES = [1, 2, 5, 10, 20, 50, 100];
function getCurrentAutoPercent(buildEvent: {
  percent: number | null;
  recordedAt: string;
}): number {
  const percent = buildEvent.percent ?? 0;
  for (let i = 0; i < AUTO_ROLLOUT_STAGES.length; i++) {
    if (percent > AUTO_ROLLOUT_STAGES[i]) continue;

    const daysAgo = differenceInDays(
      new Date(),
      parseISO(buildEvent.recordedAt)
    );
    const index = Math.min(6, i + daysAgo);

    return AUTO_ROLLOUT_STAGES[index];
  }
  return 100;
}

export default function BuildEventDetail({
  buildEvent,
  isFirst,
  invalidate,
}: Props) {
  const myUser = useMyUserClient();

  const [editing, setEditing] = React.useState(false);

  type FormValues = Omit<Api.BuildEvent.W, 'percent'> & {
    percent: string | null;
  };

  const formSubmit = useApiFormSubmit('PUT', `/build-events/${buildEvent.id}`, {
    invalidate,
  });
  const form = useForm<Partial<FormValues>>({
    defaultValues: {},
    pristineValues: {
      ...buildEvent,
      percent: buildEvent.percent != null ? String(buildEvent.percent) : null,
    },
    onSubmit: (values, helpers) =>
      formSubmit(
        {
          ...values,
          percent: values.percent ? parseInt(values.percent, 10) : undefined,
        },
        helpers
      ),
  });

  if (editing) {
    return (
      <FormWrap
        formErrorList={form.formErrorList}
        handleSubmit={form.handleSubmit}
      >
        <FieldSelect
          choices={Model.BuildEvent.TYPE_CHOICES}
          field={form.getField('type')}
        />
        <FieldText field={form.getField('percent')} />
        <FieldTextRequired field={form.getField('recordedAt')} />
        <SubmitButton {...form} label="Submit" />{' '}
        <html.a
          style={styles.cancel}
          onClick={() => {
            setEditing(false);
            form.reset();
          }}
        >
          <Text>Cancel</Text>
        </html.a>
      </FormWrap>
    );
  }

  function rendeEvent() {
    if (buildEvent.type === 'awaiting_review') {
      return (
        <>
          <i
            className="fa-regular fa-clock"
            style={{ color: 'rgb(4, 82, 130)' }}
          />{' '}
          Awaiting review
        </>
      );
    } else if (buildEvent.type === 'rejected') {
      return (
        <>
          <i
            className="fa-solid fa-ban"
            style={{ color: 'rgb(177, 13, 13)' }}
          />{' '}
          Rejected by review
        </>
      );
    } else if (buildEvent.type === 'rollout_started') {
      return (
        <>
          <i
            className="fa-solid fa-circle-half-stroke"
            style={{ color: 'rgb(4, 82, 130)' }}
          />{' '}
          Automatic rollout started (currently{' '}
          {getCurrentAutoPercent({
            percent: 0,
            recordedAt: buildEvent.recordedAt,
          })}
          % of users -{' '}
          <AccentTextLink href="/img/ios_rollout_schedule.png" target="_blank">
            schedule
          </AccentTextLink>
          )
        </>
      );
    } else if (buildEvent.type === 'rollout_paused') {
      return (
        <>
          <i
            className="fa-regular fa-circle-pause"
            style={{ color: 'rgb(4, 82, 130)' }}
          />{' '}
          Rollout paused
          {buildEvent.percent != null && ` (${buildEvent.percent}% of users)`}
        </>
      );
    } else if (buildEvent.type === 'rollout_resumed') {
      return (
        <>
          <i
            className="fa-solid fa-circle-half-stroke"
            style={{ color: 'rgb(4, 82, 130)' }}
          />{' '}
          Automatic rollout resumed
          {buildEvent.percent != null && (
            <>
              {' '}
              (currently {getCurrentAutoPercent(buildEvent)}% of users -{' '}
              <AccentTextLink
                href="/img/ios_rollout_schedule.png"
                target="_blank"
              >
                schedule
              </AccentTextLink>
              )
            </>
          )}
        </>
      );
    } else if (buildEvent.type === 'rollout_increased') {
      return (
        <>
          <i
            className="fa-solid fa-circle-half-stroke"
            style={{ color: 'rgb(4, 82, 130)' }}
          />{' '}
          Rollout increased
          {buildEvent.percent != null && ` to ${buildEvent.percent}% of users`}
        </>
      );
    } else if (buildEvent.type === 'live') {
      return (
        <>
          <i
            className="fa-regular fa-circle-check"
            style={{ color: 'rgb(26, 134, 58)' }}
          />{' '}
          Live
        </>
      );
    } else {
      return <>{buildEvent.type}</>;
    }
  }
  return (
    <html.div style={styles.event(isFirst)}>
      <Text>
        {rendeEvent()} &ndash;{' '}
        <html.span style={styles.faint}>
          <DateWithTooltip timestamp={buildEvent.recordedAt} />
        </html.span>
      </Text>
      {myUser.agencyRole && (
        <>
          &nbsp;&nbsp;
          <html.a style={styles.promptButton} onClick={() => setEditing(true)}>
            <i className="fa-regular fa-pen-to-square" />
          </html.a>
        </>
      )}
    </html.div>
  );
}

const styles = css.create({
  event: (first: boolean) => ({
    opacity: first ? 1 : 0.5,
  }),
  faint: {
    color: '#7f858b',
  },
  promptButton: {
    color: '#ccd',
    cursor: 'pointer',
  },
  cancel: {
    color: 'inherit',
    cursor: 'pointer',
  },
});
